import React from 'react';
import MobileResponsiveTable from './MobileResponsiveTable';
import { IntakeFormData } from '../../lib/types/IntakeFormTypes';
import { ColumnDef } from '@tanstack/react-table';

interface IntakeFormReadOnlyProps {
  formData: IntakeFormData;
  showHeader?: boolean;
}

interface FlattenedFormField {
  section: string;
  field: string;
  value: string;
}

const IntakeFormReadOnly: React.FC<IntakeFormReadOnlyProps> = ({ formData, showHeader = true }) => {
  // Function to flatten the nested form data into a table-friendly format
  const flattenFormData = (data: IntakeFormData): FlattenedFormField[] => {
    const flattened: FlattenedFormField[] = [];

    // Personal Information
    if (data.personalInfo) {
      const section = 'Personal Information';
      flattened.push(
        { section, field: 'Full Name', value: data.personalInfo.fullName || '' },
        { section, field: 'Email', value: data.personalInfo.email || '' },
        { section, field: 'Phone', value: data.personalInfo.phone || '' },
        { section, field: 'Date', value: data.personalInfo.date || '' },
        { section, field: 'Date of Birth', value: data.personalInfo.dob || '' },
        { section, field: 'Gender', value: data.personalInfo.gender || '' },
        { section, field: 'Address', value: data.personalInfo.address || '' },
        { section, field: 'Marital Status', value: data.personalInfo.maritalStatus || '' },
        { section, field: 'Job Status', value: data.personalInfo.jobStatus || '' },
        { section, field: 'Height', value: data.personalInfo.height || '' },
        { section, field: 'Weight', value: data.personalInfo.weight || '' },
        { section, field: 'Ethnicity', value: data.personalInfo.ethnicity || '' },
        { section, field: 'Birth Place', value: data.personalInfo.birthPlace || '' },
        { section, field: 'Emergency Contact Name', value: data.personalInfo.emergencyContactName || '' },
        { section, field: 'Emergency Contact Relationship', value: data.personalInfo.emergencyContactRelationship || '' },
        { section, field: 'Emergency Contact Phone', value: data.personalInfo.emergencyContactPhone || '' }
      );
    }

    // Health Concerns
    if (data.healthConcerns) {
      const section = 'Health Concerns';
      flattened.push(
        { section, field: 'Main Concern', value: data.healthConcerns.mainConcern || '' },
        { section, field: 'Problem First Begin', value: data.healthConcerns.problemFirstBegin || '' },
        { section, field: 'Life at the Time', value: data.healthConcerns.lifeAtTheTime || '' },
        { section, field: 'Worse or Better', value: data.healthConcerns.worseBetter || '' },
        { section, field: 'Health Goals', value: data.healthConcerns.healthGoals || '' },
        { section, field: 'Biggest Obstacle', value: data.healthConcerns.biggestObstacle || '' },
        { section, field: 'Commitment Scale', value: data.healthConcerns.commitmentScale ? `${data.healthConcerns.commitmentScale}/10` : '' },
        { section, field: 'Takes Meds for Concern', value: data.healthConcerns.takesMedsForConcern || '' }
      );
    }

    // Symptom Assessment
    if (data.symptomAssessment) {
      const section = 'Symptom Assessment';
      Object.entries(data.symptomAssessment).forEach(([symptom, ratingValue]) => {
        // Handle different types of rating values
        if (typeof ratingValue === 'object' && ratingValue !== null) {
          // Handle nested symptom objects
          Object.entries(ratingValue as Record<string, any>).forEach(([subSymptom, rating]) => {
            const shouldShow = rating && 
              (typeof rating === 'boolean' ? rating : (rating !== "" && rating !== "0"));
            
            if (shouldShow) {
              flattened.push({
                section,
                field: `${symptom} - ${subSymptom}`.replace(/([A-Z])/g, ' $1').trim(),
                value: typeof rating === 'boolean' ? (rating ? 'Yes' : 'No') : `${rating}/10`
              });
            }
          });
        } else {
          // Handle direct rating values
          const shouldShow = ratingValue && 
            (typeof ratingValue === 'boolean' ? ratingValue : (ratingValue !== "" && ratingValue !== "0"));
          
          if (shouldShow) {
            flattened.push({
              section,
              field: symptom.replace(/([A-Z])/g, ' $1').trim(),
              value: typeof ratingValue === 'boolean' ? (ratingValue ? 'Yes' : 'No') : `${ratingValue}/10`
            });
          }
        }
      });
    }

    // Note: Past Health & Medical History doesn't exist in IntakeFormTypes
    // The data seems to be spread across preExistingConditions and familyMedicalHistory

    // Lifestyle Profile
    if (data.lifestyleProfile) {
      const section = 'Lifestyle';
      flattened.push(
        { section, field: 'Education', value: data.lifestyleProfile.education || '' },
        { section, field: 'Occupation', value: data.lifestyleProfile.occupation || '' },
        { section, field: 'Schedule', value: data.lifestyleProfile.schedule || '' },
        { section, field: 'Enjoy Work', value: data.lifestyleProfile.enjoyWork || '' },
        { section, field: 'Satisfied with Compensation', value: data.lifestyleProfile.satisfiedCompensation || '' },
        { section, field: 'Mold Exposure', value: data.lifestyleProfile.moldExposure || '' }
      );

      // Live With
      if (data.lifestyleProfile.liveWith) {
        const liveWithOptions = [];
        if (data.lifestyleProfile.liveWith.myself) liveWithOptions.push('Myself');
        if (data.lifestyleProfile.liveWith.partner) liveWithOptions.push('Partner');
        if (data.lifestyleProfile.liveWith.roommates) liveWithOptions.push('Roommates');
        if (data.lifestyleProfile.liveWith.parents) liveWithOptions.push('Parents');
        if (data.lifestyleProfile.liveWith.children) liveWithOptions.push('Children');
        if (data.lifestyleProfile.liveWith.other) liveWithOptions.push(data.lifestyleProfile.liveWith.other);
        
        if (liveWithOptions.length > 0) {
          flattened.push({
            section,
            field: 'Live With',
            value: liveWithOptions.join(', ')
          });
        }
      }
    }

    // Dietary Profile
    if (data.dietaryProfile) {
      const section = 'Dietary Profile';
      flattened.push(
        { section, field: 'Foods to Not Eat', value: data.dietaryProfile.foodsToNotEat || '' },
        { section, field: 'Favorite Foods', value: data.dietaryProfile.favoriteFoods || '' },
        { section, field: 'Perfect Day of Food', value: data.dietaryProfile.perfectDayOfFood || '' },
        { section, field: 'Symptoms if Meal Missed', value: data.dietaryProfile.symptomsIfMealMissed || '' },
        { section, field: 'Symptoms After Meals', value: data.dietaryProfile.symptomsAfterMeals || '' },
        { section, field: 'Foods Avoided', value: data.dietaryProfile.foodsAvoided || '' },
        { section, field: 'Special Diet', value: data.dietaryProfile.specialDiet || '' },
        { section, field: 'Grocery Shop', value: data.dietaryProfile.groceryShop || '' },
        { section, field: 'Who Grocery Shops', value: data.dietaryProfile.whoGroceryShops || '' },
        { section, field: 'Meal Plan', value: data.dietaryProfile.mealPlan || '' },
        { section, field: 'Cook', value: data.dietaryProfile.cook || '' },
        { section, field: 'Enjoy Cooking', value: data.dietaryProfile.enjoyCooking || '' },
        { section, field: 'Read Labels', value: data.dietaryProfile.readLabels || '' },
        { section, field: 'Count Calories', value: data.dietaryProfile.countCalories || '' },
        { section, field: 'Appetite Change', value: data.dietaryProfile.appetiteChange || '' },
        { section, field: 'Meals Out Per Week', value: data.dietaryProfile.mealsOutPerWeek || '' },
        { section, field: 'Diet History', value: data.dietaryProfile.dietHistoryDescription || '' },
        { section, field: 'Past Nutritional Program', value: data.dietaryProfile.pastNutritionalProgram || '' },
        { section, field: 'Past Program Details', value: data.dietaryProfile.pastNutritionalProgramDetails || '' },
        { section, field: 'Past Program Results', value: data.dietaryProfile.pastProgramResults || '' },
        { section, field: 'Past Program Challenges', value: data.dietaryProfile.pastProgramChallenges || '' }
      );
    }

    // Sleep Profile
    if (data.sleepProfile) {
      const section = 'Sleep Profile';
      flattened.push(
        { section, field: 'Bed/Wake Time', value: data.sleepProfile.bedWakeTime || '' },
        { section, field: 'Wake in Night', value: data.sleepProfile.wakeInNight || '' },
        { section, field: 'Wake Feeling Rested', value: data.sleepProfile.wakeFeelingRested || '' },
        { section, field: 'Mouth Breathe', value: data.sleepProfile.mouthBreathe || '' },
        { section, field: 'Sleep Aids', value: data.sleepProfile.sleepAids || '' },
        { section, field: 'Sleep Retainer', value: data.sleepProfile.sleepRetainer || '' },
        { section, field: 'Sleep Disruptions', value: data.sleepProfile.sleepDisruptions || '' }
      );
    }

    // Dental Profile
    if (data.dentalProfile) {
      const section = 'Dental Profile';
      const dentalConditions = [];
      if (data.dentalProfile.mercuryFillings) dentalConditions.push('Mercury Fillings');
      if (data.dentalProfile.rootCanals) dentalConditions.push('Root Canals');
      if (data.dentalProfile.ulcersLesions) dentalConditions.push('Ulcers/Lesions');
      if (data.dentalProfile.braces) dentalConditions.push('Braces');
      if (data.dentalProfile.bruxism) dentalConditions.push('Bruxism');
      if (data.dentalProfile.toothPain) dentalConditions.push('Tooth Pain');
      if (data.dentalProfile.bleedingGums) dentalConditions.push('Bleeding Gums');
      if (data.dentalProfile.problemsChewing) dentalConditions.push('Problems Chewing');
      if (data.dentalProfile.tongueTies) dentalConditions.push('Tongue Ties');
      if (data.dentalProfile.tmj) dentalConditions.push('TMJ');
      if (data.dentalProfile.gingivitis) dentalConditions.push('Gingivitis');
      if (data.dentalProfile.compositeFillings) dentalConditions.push('Composite Fillings');
      if (data.dentalProfile.dryMouth) dentalConditions.push('Dry Mouth');
      if (data.dentalProfile.wisdomTeethPulled) dentalConditions.push('Wisdom Teeth Pulled');
      if (data.dentalProfile.badBreath) dentalConditions.push('Bad Breath');
      
      if (dentalConditions.length > 0) {
        flattened.push({
          section,
          field: 'Dental Conditions',
          value: dentalConditions.join(', ')
        });
      }
    }

    // Practitioner Questions
    if (data.practitionerQuestions) {
      const section = 'Practitioner Questions';
      flattened.push(
        { section, field: 'Seen Functional Health Pro', value: data.practitionerQuestions.seenFunctionalHealthPro || '' },
        { section, field: 'Functional Health Pro Details', value: data.practitionerQuestions.functionalHealthProDetails || '' },
        { section, field: 'Seen Family Doctor', value: data.practitionerQuestions.seenFamilyDoctor || '' },
        { section, field: 'Family Doctor Details', value: data.practitionerQuestions.familyDoctorDetails || '' }
      );
    }

    // Pre-existing Conditions
    if (data.preExistingConditions) {
      const section = 'Pre-existing Conditions';
      flattened.push(
        { section, field: 'Has Conditions', value: data.preExistingConditions.hasConditions || '' },
        { section, field: 'Conditions Details', value: data.preExistingConditions.conditionsDetails || '' },
        { section, field: 'Has Allergies', value: data.preExistingConditions.hasAllergies || '' },
        { section, field: 'Allergies Details', value: data.preExistingConditions.allergiesDetails || '' },
        { section, field: 'Has Sensitivities', value: data.preExistingConditions.hasSensitivities || '' },
        { section, field: 'Sensitivities Details', value: data.preExistingConditions.sensitivitiesDetails || '' },
        { section, field: 'Tested for Sensitivities', value: data.preExistingConditions.testedForSensitivities || '' }
      );
    }

    // Family Medical History
    if (data.familyMedicalHistory) {
      const section = 'Family Medical History';
      flattened.push(
        { section, field: 'Children', value: data.familyMedicalHistory.children || '' },
        { section, field: 'Life Changes', value: data.familyMedicalHistory.lifeChanges || '' },
        { section, field: 'Losses', value: data.familyMedicalHistory.losses || '' },
        { section, field: 'Hospitalized', value: data.familyMedicalHistory.hospitalized || '' },
        { section, field: 'Surgeries', value: data.familyMedicalHistory.surgeries || '' },
        { section, field: 'Vaccinations', value: data.familyMedicalHistory.vaccinations || '' },
        { section, field: 'Birth History', value: data.familyMedicalHistory.birthHistory || '' },
        { section, field: 'Was Breast Fed', value: data.familyMedicalHistory.wasBreastFed || '' },
        { section, field: 'Child Health', value: data.familyMedicalHistory.childHealth || '' },
        { section, field: 'Antibiotics Child/Adult', value: data.familyMedicalHistory.antibioticsChildAdult || '' }
      );

      // Stressors
      if (data.familyMedicalHistory.stressors) {
        const stressors = [];
        if (data.familyMedicalHistory.stressors.family) stressors.push(`Family: ${data.familyMedicalHistory.stressors.family}/10`);
        if (data.familyMedicalHistory.stressors.work) stressors.push(`Work: ${data.familyMedicalHistory.stressors.work}/10`);
        if (data.familyMedicalHistory.stressors.health) stressors.push(`Health: ${data.familyMedicalHistory.stressors.health}/10`);
        if (data.familyMedicalHistory.stressors.finances) stressors.push(`Finances: ${data.familyMedicalHistory.stressors.finances}/10`);
        if (data.familyMedicalHistory.stressors.social) stressors.push(`Social: ${data.familyMedicalHistory.stressors.social}/10`);
        if (data.familyMedicalHistory.stressors.other) stressors.push(`Other: ${data.familyMedicalHistory.stressors.other}`);
        
        if (stressors.length > 0) {
          flattened.push({
            section,
            field: 'Stress Levels',
            value: stressors.join(', ')
          });
        }
      }

      // Family History Table
      if (data.familyMedicalHistory.familyHistory && data.familyMedicalHistory.familyHistory.length > 0) {
        const familyMembers = data.familyMedicalHistory.familyHistory
          .map(member => `${member.member}: Age ${member.age}, ${member.status}`)
          .join('; ');
        if (familyMembers) {
          flattened.push({
            section,
            field: 'Family Members Health',
            value: familyMembers
          });
        }
      }
    }

    // Physical Activity
    if (data.physicalActivity) {
      const section = 'Physical Activity';
      flattened.push(
        { section, field: 'Activity Level', value: data.physicalActivity.activityLevel || '' },
        { section, field: 'Fatigue After Exercise', value: data.physicalActivity.fatigueAfterExercise || '' },
        { section, field: 'Further Notes', value: data.physicalActivity.furtherNotes || '' }
      );

      // Activity Table
      if (data.physicalActivity.activityTable && data.physicalActivity.activityTable.length > 0) {
        const activities = data.physicalActivity.activityTable
          .map(activity => `${activity.activity}: ${activity.daysPerWeek} days/week, ${activity.timeOfDay}, ${activity.duration}`)
          .join('; ');
        if (activities) {
          flattened.push({
            section,
            field: 'Activities',
            value: activities
          });
        }
      }
    }

    // Supplements & Medications
    if (data.supplementsMedications) {
      const section = 'Supplements & Medications';
      
      // Supplements
      if (data.supplementsMedications.supplements && data.supplementsMedications.supplements.length > 0) {
        const supplements = data.supplementsMedications.supplements
          .map(supp => `${supp.typeBrand}: ${supp.dosage}, ${supp.frequency}, ${supp.reason}, Started: ${supp.startDate}`)
          .join('; ');
        if (supplements) {
          flattened.push({
            section,
            field: 'Supplements',
            value: supplements
          });
        }
      }

      // Prescriptions
      if (data.supplementsMedications.prescriptions && data.supplementsMedications.prescriptions.length > 0) {
        const prescriptions = data.supplementsMedications.prescriptions
          .map(rx => `${rx.medication}: ${rx.dosage}, ${rx.frequency}, ${rx.reason}, ${rx.startEndDate}`)
          .join('; ');
        if (prescriptions) {
          flattened.push({
            section,
            field: 'Prescriptions',
            value: prescriptions
          });
        }
      }

      flattened.push(
        { section, field: 'Side Effects', value: data.supplementsMedications.sideEffects || '' },
        { section, field: 'Side Effects Details', value: data.supplementsMedications.sideEffectsDetails || '' },
        { section, field: 'NSAIDs Use', value: data.supplementsMedications.nsaidsUse || '' },
        { section, field: 'Tylenol Use', value: data.supplementsMedications.tylenolUse || '' },
        { section, field: 'Acid Blockers Use', value: data.supplementsMedications.acidBlockersUse || '' },
        { section, field: 'Antibiotics 3x/Year', value: data.supplementsMedications.antibiotics3xYear || '' },
        { section, field: 'Long Term Antibiotics', value: data.supplementsMedications.longTermAntibiotics || '' },
        { section, field: 'Steroids Use', value: data.supplementsMedications.steroidsUse || '' },
        { section, field: 'Other Medication', value: data.supplementsMedications.otherMedication || '' },
        { section, field: 'Other Medication Details', value: data.supplementsMedications.otherMedicationDetails || '' },
        { section, field: 'Recreational Drugs', value: data.supplementsMedications.recreationalDrugs || '' },
        { section, field: 'Recreational Drugs Details', value: data.supplementsMedications.recreationalDrugsDetails || '' },
        { section, field: 'Marijuana/CBD', value: data.supplementsMedications.marijuanaCBD || '' },
        { section, field: 'Marijuana/CBD Details', value: data.supplementsMedications.marijuanaCBDDetails || '' },
        { section, field: 'Smoke', value: data.supplementsMedications.smoke || '' },
        { section, field: 'Smoke Details', value: data.supplementsMedications.smokeDetails || '' }
      );
    }

    // Psychosocial Profile
    if (data.psychosocialProfile) {
      const section = 'Psychosocial Profile';
      const psychosocialFlags = [];
      if (data.psychosocialProfile.lessVital) psychosocialFlags.push('Feels Less Vital');
      if (data.psychosocialProfile.likeYourself) psychosocialFlags.push('Likes Self');
      if (data.psychosocialProfile.confident) psychosocialFlags.push('Confident');
      if (data.psychosocialProfile.stressReducesQuality) psychosocialFlags.push('Stress Reduces Quality');
      if (data.psychosocialProfile.meaningAndPurpose) psychosocialFlags.push('Has Meaning and Purpose');
      if (data.psychosocialProfile.likeWork) psychosocialFlags.push('Likes Work');
      if (data.psychosocialProfile.timeMoneyResponsibilities) psychosocialFlags.push('Manages Time/Money/Responsibilities');
      if (data.psychosocialProfile.difficultToTrust) psychosocialFlags.push('Difficult to Trust');
      if (data.psychosocialProfile.overwhelmed) psychosocialFlags.push('Overwhelmed');
      if (data.psychosocialProfile.practiceMeditation) psychosocialFlags.push('Practices Meditation');
      if (data.psychosocialProfile.abusedOrTrauma) psychosocialFlags.push('Experienced Abuse/Trauma');
      
      if (psychosocialFlags.length > 0) {
        flattened.push({
          section,
          field: 'Psychological Indicators',
          value: psychosocialFlags.join(', ')
        });
      }

      // Relaxation Techniques
      if (data.psychosocialProfile.relaxationTechniques) {
        const techniques = Object.entries(data.psychosocialProfile.relaxationTechniques)
          .filter(([_, value]) => value)
          .map(([technique, _]) => technique)
          .join(', ');
        if (techniques) {
          flattened.push({
            section,
            field: 'Relaxation Techniques',
            value: techniques
          });
        }
      }

      flattened.push(
        { section, field: 'Stressful Events', value: data.psychosocialProfile.stressfulEvents || '' },
        { section, field: 'Worry Most About', value: data.psychosocialProfile.worryMost || '' },
        { section, field: 'Relieve Stress', value: data.psychosocialProfile.relieveStress || '' }
      );
    }

    // Toxin Screen
    if (data.toxinScreen) {
      const section = 'Toxin Screen';
      const toxinEntries = Object.entries(data.toxinScreen)
        .filter(([_, rating]) => rating && rating !== "0")
        .map(([item, rating]) => {
          const itemName = item.replace(/([A-Z])/g, ' $1').trim();
          return `${itemName}: ${rating}/3`;
        });
      
      if (toxinEntries.length > 0) {
        flattened.push({
          section,
          field: 'Toxin Exposures',
          value: toxinEntries.join(', ')
        });
      }
    }

    // Symptom Assessment
    if (data.symptomAssessment) {
      Object.entries(data.symptomAssessment).forEach(([sectionName, symptoms]) => {
        const section = `Symptoms - ${sectionName}`;
        Object.entries(symptoms).forEach(([symptom, value]) => {
          if (value && value !== "0" && typeof value !== 'boolean') {
            flattened.push({
              section,
              field: symptom.replace(/([A-Z])/g, ' $1').trim(),
              value: typeof value === 'string' ? value : 'Yes'
            });
          }
        });
      });
    }

    // Other Conditions
    if (data.healthAndMedicalConditions) {
      const section = 'Other Health Conditions';
      flattened.push(
        { section, field: 'Other Conditions', value: data.healthAndMedicalConditions.otherConditions || '' }
      );
    }

    // Client Statement
    if (data.clientStatement) {
      const section = 'Client Statement';
      flattened.push(
        { section, field: 'Client Name', value: data.clientStatement.clientNameStatement || '' },
        { section, field: 'Date Signed', value: data.clientStatement.dateSigned || '' },
        { section, field: 'Signature', value: data.clientStatement.signature ? 'Signed' : 'Not Signed' }
      );
    }

    return flattened.filter(item => item.value); // Only show fields with values
  };

  const flattenedData = flattenFormData(formData);

  const columns: ColumnDef<FlattenedFormField>[] = [
    {
      header: 'Section',
      accessorKey: 'section',
      cell: (info) => (
        <span className="font-medium text-brand-green-700">{info.getValue() as string}</span>
      ),
    },
    {
      header: 'Field',
      accessorKey: 'field',
      cell: (info) => info.getValue(),
    },
    {
      header: 'Value',
      accessorKey: 'value',
      cell: (info) => (
        <span className="text-brand-stone-700">{info.getValue() as string}</span>
      ),
    },
  ];

  return (
    <div>
      {showHeader && (
        <div className="mb-6">
          <div className="flex items-center space-x-2">
            <svg 
              className="w-5 h-5 text-brand-green-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
              />
            </svg>
            <h2 className="text-xl font-semibold text-brand-stone-800">
              Submitted Intake Form Data
            </h2>
            <span className="text-sm text-brand-stone-500 italic">(Read-Only)</span>
          </div>
          <p className="text-sm text-brand-stone-600 mt-2">
            This data was submitted on {new Date(formData.created_at || Date.now()).toLocaleDateString()}.
            The information below is locked and cannot be edited.
          </p>
        </div>
      )}

      <MobileResponsiveTable
        data={flattenedData}
        columns={columns}
        searchPlaceholder="Search form data..."
        emptyMessage="No form data available"
        pageSize={20}
      />
    </div>
  );
};

export default IntakeFormReadOnly;